import React, { useEffect, useState } from 'react';
import { ReactComponent as CloseIco } from "../../../assets/icons/main/orderList-close.svg";
import { ReactComponent as PenIco } from "../../../assets/icons/main/orderList-pen.svg";
import { ReactComponent as DoneIco } from "../../../assets/icons/main/orderList-done.svg";
import './OrderList.css';
import axios from "axios";
import { closeSnackbar, useSnackbar } from "notistack";
import { ReactComponent as LoginCross } from "../../../assets/icons/main/login-cross.svg";
import TPSLClose from "../../../assets/icons/main/NotifiClose.png";
import {roundFractional} from "../../../features/numberRounding";
import BuyInput from '../../../components/inputs/BuyInput';
import Button from '../../../components/button/Button';
import {useTranslation} from "react-i18next";
import useIsMobile from "../useIsMobile";

const ORDER_API = "https://api-ru.fynex.cc/orders/api/order";

// ============================================================
// Общее для обеих версий (десктоп + мобильная)
// ============================================================

// Цена без экспоненты: маленькие значения с большим числом знаков, остальные — 2 знака
const formatOrderPrice = (v) => Number(v) < 1 ? Number(v).toFixed(8).replace(/\.?0+$/, "") : Number(v).toFixed(2);

// Цена ликвидации
const calcLiquidationPrice = (price, leverage, isLong) => {
    const p = Number(price);
    const liquidationProm = p / 100 * (100 / leverage);
    return isLong ? p - liquidationProm : p + liquidationProm;
};

const formatDateTime = (d) => new Date(d).toLocaleString("ru-RU", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit"
});

// PNL зелёный, если прибыль неотрицательна (одинаково для таблицы и карточки)
const isPnlPositive = (pnl, pnlPercent) => pnl >= 0 || roundFractional(Number(pnlPercent)) >= 0;

// Запросы и уведомления: изменение TP/SL и закрытие ордера
const useOrderActions = ({ id, pnl, pnlPercent, margin, requireUserData, requireUserBalance }) => {
    const {t} = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const actionSnackbar = snackbarId => (
        <button className="close-button" onClick={() => closeSnackbar(snackbarId)}>
            <LoginCross />
        </button>
    );

    const showError = (message, options = {}) => enqueueSnackbar(message, {
        action: actionSnackbar, variant: 'error', className: "snackerror", ...options
    });

    // kind: "take_profit" | "stop_loss"
    const changeLimit = (kind, value) => {
        axios.post(`${ORDER_API}/change/${kind}`, { id: id, value: value }, { headers })
            .then(() => {
                requireUserData();
            }).catch((e) => {
                console.log(e);
            });
    };

    const setCloseButtonsDisabled = (disabled) => {
        document.querySelectorAll('.cancel__buttonDEL').forEach(button => {
            button.disabled = disabled;
        });
    };

    const closeOrder = () => {
        setCloseButtonsDisabled(true);
        axios.post(`${ORDER_API}/close`, { id, pnl, pnl_percent: pnlPercent, margin: margin }, { headers })
            .then(() => {
                requireUserData();
                requireUserBalance();
                enqueueSnackbar(t("orderList.orderClosed"), { action: actionSnackbar, variant: 'success', className: "snacksuccess" });
                setTimeout(() => setCloseButtonsDisabled(false), 500);
            }).catch(() => {
                showError(t("orderList.errorOccurred"));
                setTimeout(() => setCloseButtonsDisabled(false), 500);
            });
    };

    return { changeLimit, closeOrder, showError };
};

// ============================================================
// Десктоп: ордер — строка таблицы с inline-редактированием TP/SL
// ============================================================
const OrderListDesktop = (props) => {
    const {t} = useTranslation();
    const {
        size, isLong, price, coin, coinfrom, marzha, margin, pnl, pnlPercent, leverage,
        takeProfit, stopLoss, isOpen, histoty, time, time_closed
    } = props;

    const { changeLimit, closeOrder } = useOrderActions(props);

    const [TP, setTP] = useState(takeProfit);
    const [SL, setSL] = useState(stopLoss);
    const [stateTPSL, setStateTPSL] = useState({TP: false, SL: false}); // какие поля сейчас редактируются

    // Подтягиваем значения с сервера, если они изменились снаружи
    useEffect(() => { setTP(takeProfit); }, [takeProfit]);
    useEffect(() => { setSL(stopLoss); }, [stopLoss]);

    const changeTP = (tpslState) => {
        setStateTPSL(tpslState);
        changeLimit("take_profit", TP);
    };

    const changeSL = (tpslState) => {
        setStateTPSL(tpslState);
        changeLimit("stop_loss", SL);
    };

    const liquidationPrice = calcLiquidationPrice(price, leverage, isLong);

    return (
        <li className={`OrderListActive`}>
            <div className="BTCUSDTdata">
                <div className="currency">
                    <div className={isLong ? "line line-green" : "line line-red"}></div>
                    {
                        coinfrom == "USDT" ?
                        <h2>{coin}/{coinfrom}</h2>
                        :
                        <h2>{coinfrom}/{coin}</h2>
                    }
                    <div className="progression">{leverage}x</div>
                </div>
                <h3>{roundFractional(Number(size))}</h3>    {/*Размер*/}
                <h3>{formatOrderPrice(price)}</h3>          {/*Цена входа*/}
                <h3>{roundFractional(Number(isOpen ? margin : marzha))} {coinfrom}</h3> {/*Маржа*/}
                <h3>{formatOrderPrice(liquidationPrice)}</h3>  {/*Цена ликвидации*/}

                <h3 className={isPnlPositive(pnl, pnlPercent) ? 'pnl pnl-green' : 'pnl pnl-red'}> {/*pnl*/}
                    {roundFractional(Number(pnl))} {coinfrom} ({roundFractional(Number(pnlPercent))}%)
                </h3>

                <h3 className='changeTPSLHeadline'>
                    {
                        stateTPSL.TP ?
                        <>
                            <input className='changeTPSL' type='text' value={TP ?? ""} onChange={(e) => setTP(e.target.value)}/>
                            {histoty != true && <DoneIco onClick={() => changeTP({SL: stateTPSL.SL, TP: false})}/>}
                        </>
                        :
                        <>
                            {TP}
                            {histoty != true && <PenIco onClick={() => setStateTPSL({SL: stateTPSL.SL, TP: true})}/>}
                        </>
                    }
                </h3>

                <h3 className='changeTPSLHeadline'>
                    {
                        stateTPSL.SL ?
                        <>
                            <input className='changeTPSL' type='text' value={SL ?? ""} onChange={(e) => setSL(e.target.value)}/>
                            {histoty != true && <DoneIco onClick={() => changeSL({TP: stateTPSL.TP, SL: false})}/>}
                        </>
                        :
                        <>
                            {SL}
                            {histoty != true && <PenIco onClick={() => setStateTPSL({TP: stateTPSL.TP, SL: true})}/>}
                        </>
                    }
                </h3>
            </div>
            {histoty == true &&
            <div className='historyDate'>
                <div className="createdAt">
                    <p>Открыт: </p>
                    <p className='timeAt'>{formatDateTime(time)}</p>
                </div>
                <div className="closedAt">
                    <p>Закрыт: </p>
                    <p className='timeAt'>{formatDateTime(time_closed)}</p>
                </div>
            </div>
            }
            {histoty != true && <button className="cancel__buttonDEL" onClick={closeOrder}>{t('orderList.close')}</button>}
        </li>
    );
};

// ============================================================
// Мобильная версия: карточка ордера в архитектуре старого мобильного сайта.
//   шапка:  [пара + плечо]   [TP / SL + карандаш]   [закрыть ордер]
//   данные: 3 колонки «подпись → значение» (размер/цена входа, PNL/текущая цена, маржа/ликвидация)
//   TP/SL редактируются в шторке снизу (стили .tpsl-editBlock* в OrderList.css)
// ============================================================
const OrderListMobile = (props) => {
    const {t} = useTranslation();
    const {
        size, avg, isLong, price, coin, coinfrom, marzha, margin, pnl, pnlPercent, leverage,
        takeProfit, stopLoss, isOpen, histoty, time, time_closed
    } = props;

    const { changeLimit, closeOrder, showError } = useOrderActions(props);

    const [TP, setTP] = useState(takeProfit);
    const [SL, setSL] = useState(stopLoss);
    const [stateTPSL, setStateTPSL] = useState(false); // открыта ли шторка редактирования TP/SL

    // Подтягиваем значения с сервера, если они изменились снаружи
    useEffect(() => { setTP(takeProfit); }, [takeProfit]);
    useEffect(() => { setSL(stopLoss); }, [stopLoss]);

    const norm = (v) => (v === undefined || v === null ? "" : String(v));
    const tpChanged = norm(TP) !== norm(takeProfit);
    const slChanged = norm(SL) !== norm(stopLoss);

    // Закрыть шторку без сохранения: возвращаем то, что реально стоит в ордере
    const closeSheet = () => {
        setTP(takeProfit);
        setSL(stopLoss);
        setStateTPSL(false);
    };

    const saveTPSL = () => {
        if (!tpChanged && !slChanged) {
            showError(t('orderList.noChanges'), { autoHideDuration: 3000 });
            return;
        }
        if (tpChanged) changeLimit("take_profit", TP);
        if (slChanged) changeLimit("stop_loss", SL);
        setStateTPSL(false);
    };

    const liquidationPrice = calcLiquidationPrice(price, leverage, isLong);
    const hasCurrentPrice = avg !== undefined && avg !== null && Number.isFinite(Number(avg));

    return (
        <li className="OrderCard">
            <div className={stateTPSL ? "tpsl-editBlockWrapper tpsl-editBlockWrapperActive" : "tpsl-editBlockWrapper"}>
                <div className="tpsl-editBlock">
                    <div className="headline">
                        <h2>{t('orderList.editLimits')}</h2>
                        <img src={TPSLClose} alt="" onClick={closeSheet}/>
                    </div>
                    <div className="inputs">
                        <BuyInput textData='USDT' placeholder={t('orderList.takeProfit')} setInputChange={setTP} valueInput={TP}/>
                        <BuyInput textData='USDT' placeholder={t('orderList.stopLoss')} setInputChange={setSL} valueInput={SL}/>
                    </div>
                    <Button text={t('orderList.save')} onClick={saveTPSL}/>
                </div>
            </div>

            <div className="oc-head">
                <div className="oc-currency">
                    <div className={isLong ? "line line-green" : "line line-red"}></div>
                    {coinfrom == "USDT" ? <h2>{coin}/{coinfrom}</h2> : <h2>{coinfrom}/{coin}</h2>}
                    <div className="progression">{leverage}x</div>
                </div>
                <div className="oc-tpsl">
                    TP / SL: {TP || "--"} / {SL || "--"}
                    {isOpen && <PenIco onClick={() => setStateTPSL(true)}/>}
                </div>
                {isOpen ? <button className="cancel__buttonDEL" onClick={closeOrder}><CloseIco/></button> : <span className="oc-head-gap"></span>}
            </div>

            <div className="oc-data">
                <ul>
                    <li><p>{t('orderList.size')} ({coinfrom})</p><h3>{roundFractional(Number(size))}</h3></li>
                    <li><p>{t('orderList.entryPrice')}</p><h3>{formatOrderPrice(price)}</h3></li>
                </ul>
                <ul>
                    <li>
                        <p>{t('orderList.pnlRoe')}</p>
                        <h3 className={isPnlPositive(pnl, pnlPercent) ? 'pnl pnl-green' : 'pnl pnl-red'}>
                            {roundFractional(Number(pnl))} {coinfrom} ({roundFractional(Number(pnlPercent))}%)
                        </h3>
                    </li>
                    {hasCurrentPrice && (
                        <li><p>{t('orderList.currentPrice')}</p><h3>{formatOrderPrice(avg)}</h3></li>
                    )}
                </ul>
                <ul>
                    <li><p>{t('orderList.margin')}</p><h3>{roundFractional(Number(isOpen ? margin : marzha))} {coinfrom}</h3></li>
                    <li><p>{t('orderList.liquidationPrice')}</p><h3>{formatOrderPrice(liquidationPrice)}</h3></li>
                </ul>
            </div>

            {histoty == true &&
            <div className="oc-data oc-history">
                <ul>
                    <li><p>Открыт: </p><p className="timeAt">{formatDateTime(time)}</p></li>
                </ul>
                <ul></ul>
                <ul>
                    <li><p>Закрыт: </p><p className="timeAt">{formatDateTime(time_closed)}</p></li>
                </ul>
            </div>
            }
        </li>
    );
};

// ============================================================
// Выбор раскладки: карточка (мобильный) или строка таблицы (десктоп)
// ============================================================
const OrderList = (props) => {
    const isMobile = useIsMobile();
    return isMobile ? <OrderListMobile {...props} /> : <OrderListDesktop {...props} />;
};

export default OrderList;
